import {
  Decrypter,
  Encrypter,
  armor,
  generateX25519Identity,
  identityToRecipient,
} from "age-encryption";
import { isEncryptedAttachment } from "./model";
import { movedInto } from "./paths";

/** age's own default, the work factor the age CLI uses for a passphrase. */
export const SCRYPT_WORK_FACTOR = 18;
export const MIN_PASSPHRASE_LENGTH = 8;

export class WrongPassphraseError extends Error {
  constructor() {
    super("That passphrase does not open this folder.");
  }
}
export class LockedFolderError extends Error {
  constructor(
    readonly folder: string,
    message = `Unlock “${folder}” first.`,
  ) {
    super(message);
  }
}

/**
 * A new folder key: an X25519 identity in age-keygen's file format, encrypted with the
 * passphrase. `age -d -i <folder>/.age-identity` asks for the passphrase and reads it.
 */
export async function createFolderKey(
  passphrase: string,
  workFactor = SCRYPT_WORK_FACTOR,
): Promise<{ file: string; identity: string }> {
  if (passphrase.length < MIN_PASSPHRASE_LENGTH)
    throw new Error(`Use a passphrase of at least ${MIN_PASSPHRASE_LENGTH} characters.`);
  const identity = await generateX25519Identity();
  const recipient = await identityToRecipient(identity);
  const encrypter = new Encrypter();
  encrypter.setPassphrase(passphrase);
  encrypter.setScryptWorkFactor(workFactor);
  const contents = `# created: ${new Date().toISOString()}\n# public key: ${recipient}\n${identity}\n`;
  return { file: armor.encode(await encrypter.encrypt(contents)), identity };
}

export async function openFolderKey(file: string, passphrase: string): Promise<string> {
  let sealed: Uint8Array;
  try {
    sealed = armor.decode(file);
  } catch {
    throw new Error("This folder's key file is damaged.");
  }
  const decrypter = new Decrypter();
  decrypter.addPassphrase(passphrase);
  let contents: string;
  try {
    contents = await decrypter.decrypt(sealed, "text");
  } catch {
    throw new WrongPassphraseError();
  }
  // Any age identity opens the folder, so a key replaced with the CLI keeps working.
  const identity = contents
    .split("\n")
    .map((line) => line.trim())
    .find((line) => line.startsWith("AGE-SECRET-KEY-"));
  if (!identity) throw new Error("This folder's key file holds no age identity.");
  return identity;
}

function toBase64(data: Uint8Array): string {
  let binary = "";
  for (let index = 0; index < data.length; index += 32768)
    binary += String.fromCharCode(...data.subarray(index, index + 32768));
  return btoa(binary);
}
const fromBase64 = (text: string): Uint8Array => Uint8Array.from(atob(text), (c) => c.charCodeAt(0));

interface OpenFolder {
  identity: string;
  recipient: string;
  /** Stored age file → what it holds: Markdown, or base64 for an image. */
  contents: Map<string, string>;
}

/**
 * The keys of the folders unlocked in this tab, and what their files hold. Nothing here
 * is written anywhere: locking a folder forgets its key and every decrypted file.
 */
export class FolderKeyring {
  private open = new Map<string, OpenFolder>();

  get folders(): string[] {
    return [...this.open.keys()].sort((a, b) => a.localeCompare(b));
  }
  unlocked(folder: string | null): folder is string {
    return folder !== null && this.open.has(folder);
  }
  async unlock(folder: string, keyFile: string, passphrase: string): Promise<void> {
    await this.add(folder, await openFolderKey(keyFile, passphrase));
  }
  async add(folder: string, identity: string): Promise<void> {
    this.open.set(folder, {
      identity,
      recipient: await identityToRecipient(identity),
      contents: new Map(),
    });
  }
  lock(folder: string): void {
    this.open.delete(folder);
  }
  /** Keeps the folders inside a renamed folder open under their new paths. */
  move(source: string, destination: string): void {
    for (const [folder, open] of [...this.open]) {
      const moved = movedInto(folder, source, destination);
      if (moved === folder) continue;
      this.open.delete(folder);
      this.open.set(moved, open);
    }
  }
  private folder(folder: string): OpenFolder {
    const open = this.open.get(folder);
    if (!open) throw new LockedFolderError(folder);
    return open;
  }
  /** What a stored file holds, if its folder is unlocked and it has been read. */
  cached(folder: string, stored: string): string | undefined {
    return this.open.get(folder)?.contents.get(stored);
  }
  async decrypt(folder: string, path: string, stored: string): Promise<string> {
    const open = this.folder(folder);
    const known = open.contents.get(stored);
    if (known !== undefined) return known;
    const decrypter = new Decrypter();
    decrypter.addIdentity(open.identity);
    const sealed = armor.decode(stored);
    const contents = isEncryptedAttachment(path)
      ? toBase64(await decrypter.decrypt(sealed))
      : await decrypter.decrypt(sealed, "text");
    // The folder may have locked while this was decrypting.
    if (this.open.get(folder) === open) open.contents.set(stored, contents);
    return contents;
  }
  async encrypt(folder: string, path: string, contents: string): Promise<string> {
    const open = this.folder(folder);
    const encrypter = new Encrypter();
    encrypter.addRecipient(open.recipient);
    // An image is encrypted as its bytes, so `age -d` gives back the image file itself.
    const stored = armor.encode(
      await encrypter.encrypt(isEncryptedAttachment(path) ? fromBase64(contents) : contents),
    );
    if (this.open.get(folder) === open) open.contents.set(stored, contents);
    return stored;
  }
  /** Forgets decrypted versions no stored file still has, so typing never piles them up. */
  retain(folder: string, stored: ReadonlySet<string>): void {
    const open = this.open.get(folder);
    if (!open) return;
    for (const key of open.contents.keys()) if (!stored.has(key)) open.contents.delete(key);
  }
}
